import { Signal } from '@angular/core';
import { Polygon } from 'fabric';
import { LOGICAL_CANVAS_WIDTH } from 'shared/fabric-drawing/tools';
import { BandedObject, PointerContext, SlideCapability, SlideHostContext } from '../slide-capability';

/** Which corner / page direction an affordance represents. */
export type PagingSide = 'prev' | 'next';

/** Edge of the corner-fold triangle, in logical canvas px. */
const FOLD_SIZE = 96;
/** Left / right hover-hit columns (15% of the width) — matches the SVG renderer's prevPage/nextPage zones. */
const EDGE_ZONE = 0.15 * LOGICAL_CANVAS_WIDTH;
const FOLD_FILL = '#e0e0e0';
const FOLD_STROKE = '#ffffff';

/** A fabric object carrying the binding's "never sync this" flag. */
type EphemeralPolygon = Polygon & { _yjsEphemeral?: boolean };

/**
 * BookpagePaging — on-canvas previous/next paging affordances for bookpage slides, as a
 * {@link SlideCapability} (`canvas-renderer-composition`).
 *
 * It renders hover-revealed corner folds (top-left = previous, top-right = next) as fabric objects,
 * contributed in the **overlay** z-band (above drawings) via {@link contributeObjects}; the host owns
 * canvas membership + re-assertion after the binding's `clear()` (Decision C6), so this capability no
 * longer runs its own `after:render` loop. Hover + activation are driven by the host's pointer
 * claim-chain (Decision C12) through {@link claims}/{@link onPointerDown}/{@link onPointerMove}, using
 * canvas-level POINTER hit-testing (works regardless of `skipTargetFind`, which the default tool sets
 * for grab-to-pan). The folds are flagged `_yjsEphemeral` (never synced) + `excludeFromExport`, and
 * pinned to the viewport top via {@link onViewportChange} so they stay put while the page scrolls.
 *
 * Availability (`hasPreviousBookpage` / `hasNextBookpage`) is read live from host-supplied signals
 * (Decision C13), so the contributed fold set reflects current availability without a separate setter;
 * the host triggers a re-render when availability changes. Activation routes through `onActivate(side)`
 * → the host's previous/next outputs (the Y.Doc bookpage-selection path is unchanged).
 */
export class BookpagePaging implements SlideCapability {
    private prevFold?: EphemeralPolygon;
    private nextFold?: EphemeralPolygon;
    private hovered: PagingSide | null = null;
    private offsetY = 0;

    constructor(
        private readonly ctx: SlideHostContext,
        private readonly onActivate: (side: PagingSide) => void,
        private readonly hasPreviousPage: Signal<boolean>,
        private readonly hasNextPage: Signal<boolean>,
    ) {}

    /** Paging input is live only for a WRITER in a non-freehand tool mode (default / selection). */
    private isInteractive(): boolean {
        const tool = this.ctx.interaction.activeTool();
        return this.ctx.interaction.isWriter() && (tool === null || tool === 'selection');
    }

    /** New slide: clear stale hover (availability is read live from the signals; the slide arg is unused). */
    onSlideChange(): void {
        this.setHover(null);
    }

    /**
     * Overlay-band folds for the currently-available sides. Writers only — non-writer apps
     * (live-session / student) show no paging affordances (parity with the old writer-gated creation).
     * Instances are created lazily and kept owned here (hover opacity + scroll-pin mutate them); the
     * host adds/removes them to match and re-asserts them after the binding's `clear()`.
     */
    contributeObjects(): BandedObject[] {
        if (!this.ctx.interaction.isWriter()) {
            this.prevFold = this.nextFold = undefined;
            return [];
        }
        const out: BandedObject[] = [];
        if (this.hasPreviousPage()) {
            this.prevFold ??= this.createFold('prev');
            out.push({ object: this.prevFold, band: 'overlay' });
        } else {
            this.prevFold = undefined;
        }
        if (this.hasNextPage()) {
            this.nextFold ??= this.createFold('next');
            out.push({ object: this.nextFold, band: 'overlay' });
        } else {
            this.nextFold = undefined;
        }
        // Drop stale hover if the hovered side is no longer available.
        if (this.hovered && !(this.hovered === 'prev' ? this.hasPreviousPage() : this.hasNextPage())) {
            this.setHover(null);
        }
        return out;
    }

    /** Claims the pointer when interactive and over an available corner (viewport coords). */
    claims(pointer: PointerContext): boolean {
        return this.isInteractive() && this.hitTestSide(pointer.viewportPoint) !== null;
    }

    onPointerDown(pointer: PointerContext): void {
        const side = this.hitTestSide(pointer.viewportPoint);
        if (side) this.onActivate(side);
    }

    onPointerMove(pointer: PointerContext): void {
        const side = this.isInteractive() ? this.hitTestSide(pointer.viewportPoint) : null;
        this.setHover(side);
    }

    /** Keep the folds pinned to the viewport top as the page scrolls (VPT ty = -offset). */
    onViewportChange(offsetY: number): void {
        this.offsetY = offsetY;
        this.prevFold?.set({ top: offsetY });
        this.nextFold?.set({ top: offsetY });
    }

    /** Which available corner the pointer is over, or null. */
    private hitTestSide(point: { x: number; y: number }): PagingSide | null {
        if (this.hasPreviousPage() && point.x <= EDGE_ZONE) return 'prev';
        if (this.hasNextPage() && point.x >= LOGICAL_CANVAS_WIDTH - EDGE_ZONE) return 'next';
        return null;
    }

    private setHover(side: PagingSide | null): void {
        if (side === this.hovered) return;
        this.hovered = side;
        this.prevFold?.set({ opacity: side === 'prev' ? 1 : 0 });
        this.nextFold?.set({ opacity: side === 'next' ? 1 : 0 });
        // Restore the canvas's own default cursor (not the literal 'default') — don't fight the
        // host's grab-to-pan cursor, which fabric re-applies before each mouse:move.
        this.ctx.canvas.setCursor(side ? 'pointer' : this.ctx.canvas.defaultCursor);
        this.ctx.canvas.requestRenderAll();
    }

    private createFold(side: PagingSide): EphemeralPolygon {
        // Right triangle occupying the top-left (prev) or top-right (next) of its own bounding box;
        // positioned at the matching canvas corner.
        const points =
            side === 'prev'
                ? [
                      { x: 0, y: 0 },
                      { x: FOLD_SIZE, y: 0 },
                      { x: 0, y: FOLD_SIZE },
                  ]
                : [
                      { x: 0, y: 0 },
                      { x: FOLD_SIZE, y: 0 },
                      { x: FOLD_SIZE, y: FOLD_SIZE },
                  ];
        const fold = new Polygon(points, {
            originX: 'left',
            originY: 'top',
            left: side === 'prev' ? 0 : LOGICAL_CANVAS_WIDTH - FOLD_SIZE,
            top: this.offsetY,
            fill: FOLD_FILL,
            stroke: FOLD_STROKE,
            strokeWidth: 1,
            opacity: this.hovered === side ? 1 : 0,
            selectable: false,
            evented: false,
            hoverCursor: 'pointer',
            objectCaching: false,
            excludeFromExport: true,
        }) as EphemeralPolygon;
        fold._yjsEphemeral = true;
        return fold;
    }

    dispose(): void {
        if (this.prevFold) this.ctx.canvas.remove(this.prevFold);
        if (this.nextFold) this.ctx.canvas.remove(this.nextFold);
        this.prevFold = this.nextFold = undefined;
    }
}
